import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Control, Resolver } from 'react-hook-form';
import {
  CUSTOMER_LANGUAGES,
  SECURITY_QUESTIONS,
  customerInfoSchema,
  customerTagsSchema,
} from '@app/shared';
import type { AuthorizedUserDto, CustomerDto, CustomerInfoInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { errorMessage } from '../../lib/api';
import { useUpdateInfo, useUpdateTags } from './api';

interface AuthorizedUserForm {
  name: string;
  relationship: string;
  dob: string;
  ssnLast4: string;
  password: string;
  secretQuestion: string;
  secretAnswer: string;
}
interface InfoForm {
  language: (typeof CUSTOMER_LANGUAGES)[number];
  firstName: string;
  middleName: string;
  lastName: string;
  contactNumber: string;
  alternateNumber: string;
  email: string;
  ssnLast4: string;
  dob: string;
  secretQuestion: string;
  secretAnswer: string;
  subscriberId: string;
  authorizedUser: AuthorizedUserForm;
  authorizedUser2: AuthorizedUserForm;
}
const authorizedUserValues = (u: AuthorizedUserDto): AuthorizedUserForm => ({
  name: u.name,
  relationship: u.relationship,
  dob: u.dob ?? '',
  ssnLast4: u.ssnLast4 ?? '',
  password: u.password,
  secretQuestion: u.secretQuestion,
  secretAnswer: u.secretAnswer,
});

const SubTitle = ({ children }: { children: string }) => (
  <Typography variant="subtitle2" component="h3" sx={{ gridColumn: '1 / -1', mt: 0.5 }}>
    {children}
  </Typography>
);
const dateProps = { inputLabel: { shrink: true } } as const;
const last4 = { htmlInput: { maxLength: 4, inputMode: 'numeric' as const } };

/**
 * The reference's five questions; a question stored before the list existed stays on offer
 * for that customer, so opening the form does not lose it.
 */
const questionOptions = (current: string) => [
  { value: '', label: 'Select Question' },
  ...toOptions(SECURITY_QUESTIONS),
  ...(current && !(SECURITY_QUESTIONS as readonly string[]).includes(current)
    ? [{ value: current, label: current }]
    : []),
];

/** The seven fields of "Authorized User1" and "Authorize User2". */
function AuthorizedUserFields({
  control,
  name,
  identity,
  question,
}: {
  control: Control<InfoForm>;
  name: 'authorizedUser' | 'authorizedUser2';
  identity: boolean;
  question: string;
}) {
  return (
    <>
      <TextInput
        control={control}
        name={`${name}.name`}
        label="A/U Name"
        slotProps={{ htmlInput: { maxLength: 60 } }}
      />
      <TextInput
        control={control}
        name={`${name}.relationship`}
        label="A/U Relationship"
        slotProps={{ htmlInput: { maxLength: 20 } }}
      />
      {identity ? (
        <>
          <TextInput
            control={control}
            name={`${name}.dob`}
            label="A/U DOB (YYYY-MM-DD)"
            type="date"
            slotProps={dateProps}
          />
          <TextInput
            control={control}
            name={`${name}.ssnLast4`}
            label="A/U SSN (Last 4 digits)"
            slotProps={last4}
          />
        </>
      ) : null}
      <TextInput
        control={control}
        name={`${name}.password`}
        label="A/U Password"
        autoComplete="off"
        slotProps={{ htmlInput: { maxLength: 30 } }}
      />
      <SelectInput
        control={control}
        name={`${name}.secretQuestion`}
        label="A/U S/Q"
        options={questionOptions(question)}
      />
      <TextInput
        control={control}
        name={`${name}.secretAnswer`}
        label="AU S/Q Answer"
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
    </>
  );
}

/**
 * Customer Information (PC377), field for field as on the reference (appendix 13.5), with
 * two differences: the customer's own Password is left out (it is disabled there, and a
 * customer has no sign-in here), and "Phone Number" is this application's contact number
 * next to "Alternate Ph". SSN and DOB show, and are sent, only for a user with PC253.
 */
export function CustomerInfoForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const updateInfo = useUpdateInfo(customer.id);
  const identity = customer.identity.visible;
  const { control, handleSubmit, setError } = useForm<InfoForm>({
    defaultValues: {
      language: customer.language,
      firstName: customer.firstName,
      middleName: customer.middleName,
      lastName: customer.lastName,
      contactNumber: customer.contactNumber,
      alternateNumber: customer.alternateNumber,
      email: customer.email,
      ssnLast4: customer.identity.ssnLast4 ?? '',
      dob: customer.identity.dob ?? '',
      secretQuestion: customer.secretQuestion,
      secretAnswer: customer.secretAnswer,
      subscriberId: customer.subscriberId,
      authorizedUser: authorizedUserValues(customer.authorizedUser),
      authorizedUser2: authorizedUserValues(customer.authorizedUser2),
    },
    resolver: zodResolver(customerInfoSchema) as unknown as Resolver<InfoForm>,
  });

  const submit = handleSubmit(async (values) => {
    // The resolver hands over the parsed schema output: an emptied number arrives as
    // undefined, which the API reads as "clear it". Without PC253 the SSN and DOB fields are
    // not on the form, and are left out so the API leaves them alone.
    const input = values as CustomerInfoInput;
    if (!identity) {
      delete input.ssnLast4;
      delete input.dob;
      for (const user of [input.authorizedUser, input.authorizedUser2]) {
        if (!user) continue;
        delete user.ssnLast4;
        delete user.dob;
      }
    }
    try {
      await updateInfo.mutateAsync(input);
      notify.success('Customer information updated.');
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      aria-label="Customer Information"
    >
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        <SelectInput
          control={control}
          name="language"
          label="Preferred Language"
          options={toOptions(CUSTOMER_LANGUAGES)}
        />
        <Box sx={{ display: { xs: 'none', sm: 'block' }, gridColumn: { md: 'span 2' } }} />
        <TextInput
          control={control}
          name="firstName"
          label="First Name"
          required
          slotProps={{ htmlInput: { maxLength: 30 } }}
        />
        <TextInput
          control={control}
          name="middleName"
          label="Middle Name"
          slotProps={{ htmlInput: { maxLength: 30 } }}
        />
        <TextInput
          control={control}
          name="lastName"
          label="Last Name"
          required
          slotProps={{ htmlInput: { maxLength: 30 } }}
        />
        {identity ? (
          <>
            <TextInput
              control={control}
              name="ssnLast4"
              label="SSN Number (Last 4 digits)"
              slotProps={last4}
            />
            <TextInput
              control={control}
              name="dob"
              label="DOB(YYYY-MM-DD)"
              type="date"
              slotProps={dateProps}
            />
          </>
        ) : null}
        <TextInput
          control={control}
          name="contactNumber"
          label="Phone Number"
          slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
        />
        <TextInput
          control={control}
          name="alternateNumber"
          label="Alternate Ph"
          slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
        />
        <SelectInput
          control={control}
          name="secretQuestion"
          label="Customer Security Question"
          options={questionOptions(customer.secretQuestion)}
        />
        <TextInput
          control={control}
          name="secretAnswer"
          label="Answer"
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextInput
          control={control}
          name="email"
          label="Email"
          type="email"
          slotProps={{ htmlInput: { maxLength: 120 } }}
        />

        <SubTitle>NLAD Details</SubTitle>
        <TextInput
          control={control}
          name="subscriberId"
          label="Subscriber ID"
          slotProps={{ htmlInput: { maxLength: 30 } }}
        />

        <SubTitle>Authorized User1</SubTitle>
        <AuthorizedUserFields
          control={control}
          name="authorizedUser"
          identity={identity}
          question={customer.authorizedUser.secretQuestion}
        />
        <SubTitle>Authorize User2</SubTitle>
        <AuthorizedUserFields
          control={control}
          name="authorizedUser2"
          identity={identity}
          question={customer.authorizedUser2.secretQuestion}
        />
      </Box>
      <Button type="submit" variant="contained" disabled={updateInfo.isPending} sx={{ mt: 2 }}>
        Update
      </Button>
    </Box>
  );
}

/**
 * Tag (PC759), as on the reference: "Tag #" (at most 50 characters) and "Add"; a customer
 * that has a tag shows it with "Delete" instead. A customer has one tag or none. Advanced
 * Search finds the customer by it.
 */
export function TagsForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const updateTags = useUpdateTags(customer.id);
  const current = customer.tags[0] ?? '';
  const [tag, setTag] = useState('');
  const [problem, setProblem] = useState('');

  const save = async (tags: string[], done: string) => {
    const parsed = customerTagsSchema.safeParse({ tags });
    if (!parsed.success) return setProblem(parsed.error.issues[0]?.message ?? 'Invalid tag');
    setProblem('');
    try {
      await updateTags.mutateAsync(parsed.data);
      setTag('');
      notify.success(done);
    } catch (err) {
      setProblem(errorMessage(err));
    }
  };

  return (
    <Box
      component="form"
      noValidate
      aria-label="Tag"
      onSubmit={(e) => {
        e.preventDefault();
        if (current) void save([], `Tag ${current} deleted.`);
        else void save([tag.trim()], `Tag ${tag.trim()} added.`);
      }}
      sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
    >
      <TextField
        label="Tag #"
        required={!current}
        value={current || tag}
        onChange={(e) => {
          setTag(e.target.value);
          setProblem('');
        }}
        error={Boolean(problem)}
        helperText={problem || undefined}
        slotProps={{ htmlInput: { maxLength: 50 }, input: { readOnly: Boolean(current) } }}
        sx={{ width: 320 }}
      />
      <Button
        type="submit"
        variant="contained"
        color={current ? 'error' : 'primary'}
        disabled={updateTags.isPending}
      >
        {current ? 'Delete' : 'Add'}
      </Button>
    </Box>
  );
}
